/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: jackpotStats.ts
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  26.09.22 3:20 pm.
 * Last modified: 26.09.22 3:20 pm.
 *
 * More information at: https://egt-digital.com/
 */
import * as config from '../config';
import store from '../eventStore';
import IJackpotStat, { IJackpotValues } from '../interfaces/IJackpotStat';
import { formatCurrencyValue, pipe } from './helpers';

export const parseJackpotResponse = (
    jackpotResponse: any,
    currency: string,
    units: number,
): Map<string, IJackpotStat> => {
    const result = new Map<string, IJackpotStat>();
    jackpotResponse.forEach(
        (jpStat: {
            instanceName: string;
            baseDenomination: number;
            levelStats: Array<{
                levelId: number;
                totalWins: number;
                currentValue: any[];
                lastWinValue?: any[];
                maxWinValue?: any[];
                lastWinDate?: string;
                maxWinDate?: string;
            }>;
            supportedCurrencies: string[];
            baseDenominationCurrencyMap: any;
        }) => {
            if (!result.has(jpStat.instanceName)) {
                result.set(jpStat.instanceName, {
                    jackpotName: jpStat.instanceName,
                    denominations: new Map<number, IJackpotValues[]>(),
                    minDenomination: 1000000,
                    currency,
                    currencies: jpStat.supportedCurrencies,
                } as IJackpotStat);
            }

            const storedJpStat = result.get(jpStat.instanceName);

            if (storedJpStat && jpStat.baseDenominationCurrencyMap[currency]) {
                storedJpStat.denominations.set(
                    jpStat.baseDenominationCurrencyMap[currency],
                    jpStat.levelStats
                        .sort((a: { levelId: number }, b: { levelId: number }) => a.levelId - b.levelId)
                        .map<IJackpotValues>(level => {
                            const currentValue = level.currentValue.find(cv => cv.key === currency)?.value || 0;
                            const lastWinValue = level.lastWinValue?.find(cv => cv.key === currency)?.value || 0;
                            const maxWinValue = level.maxWinValue?.find(cv => cv.key === currency)?.value || 0;
                            return {
                                level: level.levelId,
                                value: currentValue,
                                formattedValue: formatCurrencyValue(currentValue, units),
                                lastWinValue,
                                formattedLastWinValue: formatCurrencyValue(lastWinValue, units),
                                maxWinValue,
                                formattedMaxWinValue: formatCurrencyValue(maxWinValue, units),
                                lastWinDate: new Date(level.lastWinDate || 0),
                                maxWinDate: new Date(level.maxWinDate || 0),
                                totalWins: level.totalWins,
                            };
                        }),
                );
                if (storedJpStat.minDenomination > jpStat.baseDenominationCurrencyMap[currency]) {
                    storedJpStat.minDenomination = jpStat.baseDenominationCurrencyMap[currency];
                }
            }
        },
    );
    // TODO: this is getting called multiple times. checkout why?
    return result;
};

const addEGTCurrency = (jpInstances: any[]): any[] => {
    jpInstances.forEach(instance => {
        const selectedCurrency = instance.levelStats[0].currentValue.map((vals: any) => vals.key).sort()[0];
        instance.levelStats.forEach((level: any) => {
            if (level.currentValue && level.currentValue.length > 0) {
                level.currentValue.push({
                    ...level.currentValue.find((instVal: any) => instVal.key === selectedCurrency),
                    key: 'EGT',
                });
            }
            if (level.lastWinValue && level.lastWinValue.length > 0) {
                level.lastWinValue.push({
                    ...level.lastWinValue[0],
                    key: 'EGT',
                });
            }
            if (level.maxWinValue && level.maxWinValue.length > 0) {
                level.maxWinValue.push({
                    ...level.maxWinValue[0],
                    key: 'EGT',
                });
            }
        });
        const denomMapKeys = Object.keys(instance.baseDenominationCurrencyMap).sort();
        if (denomMapKeys.length > 0) {
            instance.baseDenominationCurrencyMap.EGT = instance.baseDenominationCurrencyMap[selectedCurrency];
        }
        instance.supportedCurrencies.push('EGT');
    });
    return jpInstances;
};

export const jackpotStats = (): void => {
    const settings = store.SETTINGS.value;
    const request = new XMLHttpRequest();
    request.addEventListener('load', function(): void {
        const jpResponse = JSON.parse(this.responseText);
        const instanceStats = pipe(addEGTCurrency)(jpResponse?.jackpotInstancesStats?.instanceStats);
        store.COMMUNICATION_CHANNEL.next({
            event: 'jpstats',
            value: instanceStats || [],
        });
    });
    request.addEventListener('error', (event): void => {
        store.EXTERNAL_CHANNEL.next({
            event: 'error',
            value: new Error('LOBBY.FETCH_JP_STATS_FAILED'),
        });
    });
    request.open('GET', config.getJackpotStatsUrl(settings.apiHost));
    request.send();
};
